// Ben-reviewed release policy: use a real modern destination before retiring
// a legacy stub. Targets below are current public CMS pages, not account pages.
import { legacyLpDisposition } from "./legacy-lp-readiness.mjs";
// The global publication retains its /en prefix. The unprefixed path and
// /us variant have no published owner; both must use this real global page.
export const GLOBAL_HOST_SIGNUP_TARGET = "/en/get-paid-to-drive-your-classic-car";
export const COUNTRY_HOST_SIGNUP_TARGETS = Object.freeze({
  uk: "/uk/get-paid-to-drive-your-classic-car",
  ie: "/ie/get-paid-to-drive-your-classic-car",
  dk: "/dk/tjen-penge-pa-at-kore-din-egen-klassiske-eller-veteranbil",
  // The older onboarding slug exists only in DEV. Use the published Swedish
  // marketing owner shared by production and development.
  se: "/se/fa-betalt-att-kora-din-klassiska-bil",
  // NZ's stored primary is inactive and Malta has no current CMS owner.
  // Old served HTML is not evidence of a current publishable source.
  nz: GLOBAL_HOST_SIGNUP_TARGET,
  au: "/au/get-paid-to-drive-your-classic-car",
  ca: "/ca/get-paid-to-drive-your-classic-car",
  za: "/za/get-paid-to-drive-your-classic-car",
  mt: GLOBAL_HOST_SIGNUP_TARGET,
  us: GLOBAL_HOST_SIGNUP_TARGET,
  fi: "/fi/get-paid-to-drive-your-classic-car",
});
// Only the listed national review stubs have no distinct publishable body. The real
// global /reviews page is public, active and has a full body; never redirect
// these to /dashboard/reviews (which requires account access) or to themselves.
// /au/reviews is a published Australian CMS page, not a stub. Keep it out of
// this early dispatcher so the source-backed CMS publication can own it.
export const RELEASE_REDIRECT_MAP = Object.freeze({
  "/pt/Aluguer de limousine no Porto": { kind: "fixed", target: "/pt/aluguer-de-limousines" },
  "/in/about": { kind: "fixed", target: "/in" },
  "/in/how-it-works": { kind: "fixed", target: "/in" },
  "/in/contact": { kind: "fixed", target: "/in" },
  "/in/insurance": { kind: "fixed", target: "/in" },
  "/in/faq": { kind: "fixed", target: "/in" },
  "/in/help": { kind: "fixed", target: "/in" },
  "/join-us": { kind: "country-host-signup", fallback: GLOBAL_HOST_SIGNUP_TARGET },
  "/de/join-us-free": { kind: "country-host-signup", fallback: GLOBAL_HOST_SIGNUP_TARGET },
  "/pt/reviews": { kind: "fixed", target: "/reviews" },
  "/dk/reviews": { kind: "fixed", target: "/reviews" },
  "/fr/reviews": { kind: "fixed", target: "/reviews" },
  "/de/reviews": { kind: "fixed", target: "/reviews" },
  "/it/reviews": { kind: "fixed", target: "/reviews" },
  "/es/reviews": { kind: "fixed", target: "/reviews" },
  "/se/reviews": { kind: "fixed", target: "/reviews" },
  "/ca/reviews": { kind: "fixed", target: "/reviews" },
  "/za/reviews": { kind: "fixed", target: "/reviews" },
  "/mt/reviews": { kind: "fixed", target: "/reviews" },
});
const COUNTRY_ROOTS = new Set([
  ...Object.keys(COUNTRY_HOST_SIGNUP_TARGETS), "de", "fr", "it", "es", "pt",
  "nl", "no", "at", "cz", "in",
]);
const countryRoot = value => {
  const root = String(value || "").replace(/^\/+|\/+$/g, "").toLowerCase();
  return COUNTRY_ROOTS.has(root) ? root : "";
};
export function normalizeReleaseRoutePath(pathname) {
  let path = String(pathname || "").split(/[?#]/)[0].replace(/\/+$/, "") || "/";
  try { return decodeURI(path); } catch { return ""; }
}
export function releaseRedirectRule(pathname) {
  const path = normalizeReleaseRoutePath(pathname);
  if (RELEASE_REDIRECT_MAP[path]) return RELEASE_REDIRECT_MAP[path];
  const match = /^\/([a-z]{2})(?:\/[a-z]{2})?\/join-us(?:-free|-for-free)?$/i.exec(path);
  return match && countryRoot(match[1])
    ? { kind: "country-host-signup", fallback: GLOBAL_HOST_SIGNUP_TARGET } : null;
}
export function resolveReleaseRouteRedirect(pathname, { countryRoot: requestRoot = "" } = {}) {
  const rawPath = String(pathname || "").split(/[?#]/)[0];
  const scoped = legacyLpDisposition(rawPath);
  if (scoped) return scoped.kind === "redirect" ? scoped.target : null;
  // The directory alias must reach the primary in one hop, rather than retain
  // a trailing directory delimiter and require a second canonical redirect.
  if (/^\/lp\/[^/]/.test(rawPath)) return rawPath.slice(3).replace(/\/+$/, "") || "/";
  const path = normalizeReleaseRoutePath(pathname);
  const rule = releaseRedirectRule(path);
  if (!rule) return null;
  if (rule.kind === "fixed") return rule.target;
  // Explicit path country wins over geolocation or a preceding page.
  const pathRoot = countryRoot(path.split("/")[1]);
  return COUNTRY_HOST_SIGNUP_TARGETS[pathRoot || countryRoot(requestRoot)] || rule.fallback;
}
export function releaseRequestCountryRoot(req) {
  const authority = String(req.headers?.["x-forwarded-host"] || req.headers?.host || "")
    .split(",")[0].trim().replace(/:\d+$/, "").replace(/^www\./, "").toLowerCase();
  const nationalHost = /^bookaclassic\.([a-z]{2})$/.exec(authority);
  if (nationalHost && countryRoot(nationalHost[1])) return countryRoot(nationalHost[1]);
  // A first-party referring country root is explicit browsing intent and
  // therefore wins over IP-country. An unrelated Referer cannot select it.
  try {
    const ref = new URL(String(req.headers?.referer || ""));
    if (ref.hostname.replace(/^www\./, "").toLowerCase() === authority) {
      const root = countryRoot(ref.pathname.split("/")[1]);
      if (root) return root;
    }
  } catch { /* No valid first-party referring country root. */ }
  const code = String(req.headers?.["cf-ipcountry"] || req.headers?.["x-vercel-ip-country"] ||
    req.headers?.["x-country"] || "").toLowerCase();
  return countryRoot(code === "gb" ? "uk" : code);
}
